import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Project } from '../../../../types/project';
import { IOSFormToggle } from '../ios/IOSFormGroup';
import { useTranslation } from 'react-i18next';
import { trackModalEvents, trackProjectEvents } from '../../../../lib/analyticsEnhanced';

interface ProjectMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMerge: (targetProjectId: string, keepSourceContent: boolean) => Promise<boolean>;
  sourceProjectId: string;
  sourceProjectTitle: string;
  projects: Project[];
}

export const ProjectMergeModal: React.FC<ProjectMergeModalProps> = ({
  isOpen,
  onClose,
  onMerge,
  sourceProjectId,
  sourceProjectTitle,
  projects
}) => {
  const { t } = useTranslation(['v3/projects', 'common']);

  // 过滤掉源项目和默认项目
  const availableProjects = projects.filter(p =>
    p.id !== sourceProjectId && p.title !== t('v3/projects:defaultProject')
  );

  // 状态
  const [targetProjectId, setTargetProjectId] = useState<string>('');
  const [keepSourceContent, setKeepSourceContent] = useState<boolean>(false);
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 当模态框打开时重置状态
  useEffect(() => {
    if (isOpen) {
      setTargetProjectId('');
      setKeepSourceContent(false);
      setError(null);

      // 记录模态框打开事件
      trackModalEvents.modalOpened('project_merge', 'button', {
        source_project_id: sourceProjectId,
        available_targets: availableProjects.length
      });
    }
  }, [isOpen, sourceProjectId, availableProjects.length]);

  // 处理合并
  const handleMerge = async () => {
    // 验证选择了目标项目
    if (!targetProjectId) {
      setError(t('v3/projects:merge.selectTargetError'));
      return;
    }

    setIsMerging(true);
    setError(null);

    try {
      const success = await onMerge(targetProjectId, keepSourceContent);
      if (success) {
        // 记录项目合并成功事件
        trackProjectEvents.projectMerged(sourceProjectId, targetProjectId, {
          keep_source_content: keepSourceContent,
          source_project_title: sourceProjectTitle
        });
        onClose();
      } else {
        setError(t('v3/projects:errors.mergeFailed'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('v3/projects:errors.mergeFailed'));
    } finally {
      setIsMerging(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    onClose();
  };

  // 处理背景点击
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-black/50"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md mx-4 bg-white dark:bg-ios-dark-gray6 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden"
          >
            {/* 模态框标题 */}
            <div className="px-4 py-3 border-b border-ios-gray5 dark:border-ios-dark-gray4">
              <h2 className="text-ios-title font-semibold text-center">{t('v3/projects:merge.title')}</h2>
            </div>

            {/* 模态框内容 */}
            <div className="p-4">
              {/* 错误提示 */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-ios-md text-ios-subhead">
                  {error}
                </div>
              )}

              {/* 源项目信息 */}
              <div className="mb-4">
                <div className="text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/projects:merge.sourceProject')}
                </div>
                <div className="p-3 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md text-ios-body">
                  {sourceProjectTitle}
                </div>
              </div>

              {/* 目标项目选择 */}
              <div className="mb-4">
                <label className="block text-ios-subhead font-medium text-ios-gray dark:text-ios-dark-gray mb-1">
                  {t('v3/projects:merge.targetProject')}
                </label>
                <select
                  value={targetProjectId}
                  onChange={(e) => setTargetProjectId(e.target.value)}
                  className="w-full h-10 px-3 bg-ios-gray5 dark:bg-ios-dark-gray4 rounded-ios-md text-ios-body focus:outline-none"
                >
                  <option value="">{t('v3/projects:merge.selectTargetPlaceholder')}</option>
                  {availableProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {t('v3/projects:merge.targetProjectHint')}
                </p>
              </div>

              {/* 保留源项目内容选项 */}
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-ios-body">{t('v3/projects:merge.keepSourceContent')}</div>
                  <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                    {t('v3/projects:merge.keepSourceContentHint')}
                  </p>
                </div>
                <IOSFormToggle
                  checked={keepSourceContent}
                  onChange={setKeepSourceContent}
                />
              </div>

              {/* 警告提示 */}
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-ios-md text-ios-footnote">
                {t('v3/projects:merge.warning')}
              </div>
            </div>

            {/* 模态框按钮 */}
            <div className="flex border-t border-ios-gray5 dark:border-ios-dark-gray4">
              <button
                onClick={handleCancel}
                className="flex-1 py-3 text-ios-body text-ios-gray dark:text-ios-dark-gray font-medium border-r border-ios-gray5 dark:border-ios-dark-gray4"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                onClick={handleMerge}
                disabled={isMerging}
                className="flex-1 py-3 text-ios-body text-ios-red dark:text-ios-dark-red font-medium"
              >
                {isMerging ? (
                  <span className="inline-block w-5 h-5 border-2 border-ios-red/30 dark:border-ios-dark-red/30 border-t-ios-red dark:border-t-ios-dark-red rounded-full animate-spin mx-auto"></span>
                ) : (
                  t('v3/projects:actions.mergeProject')
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
